import { useState } from 'react';
import { Paperclip, Download } from 'lucide-react';
import { useAccessToken } from '@/auth/useAccessToken';
import { attachmentsApi } from './attachmentsApi';
import { AuthImage } from './AuthImage';
import { useLightbox } from './Lightbox';
import type { Attachment } from '@/features/tickets/schema';

const isImage = (a: Attachment) => a.contentType.startsWith('image/');

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Read-only display of a message's / ticket's attachments. */
export function AttachmentList({ attachments }: { attachments: Attachment[] }) {
  // Inline images are rendered within the message body — don't repeat them here.
  const listed = (attachments ?? []).filter((a) => !a.isInline);
  if (listed.length === 0) return null;
  const images = listed.filter(isImage);
  const files = listed.filter((a) => !isImage(a));

  return (
    <div className="mt-3 space-y-2">
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {images.map((a) => (
            <ImageThumb key={a.id} att={a} />
          ))}
        </div>
      )}
      {files.map((a) => (
        <FileChip key={a.id} att={a} />
      ))}
    </div>
  );
}

function useDownloader() {
  const getAccessToken = useAccessToken();
  const [busy, setBusy] = useState(false);
  const download = async (att: Attachment) => {
    setBusy(true);
    try {
      const blob = await attachmentsApi.fetchBlob(att.id, getAccessToken);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } finally {
      setBusy(false);
    }
  };
  return { download, busy };
}

function ImageThumb({ att }: { att: Attachment }) {
  const lightbox = useLightbox();
  return (
    <button
      type="button"
      onClick={() => lightbox.open(att.id, att.fileName)}
      title={`${att.fileName} (${formatSize(att.sizeBytes)}) — click to enlarge`}
      className="block rounded-md border overflow-hidden hover:ring-2 ring-primary/40 cursor-zoom-in"
    >
      <AuthImage id={att.id} alt={att.fileName} className="h-28 w-28 object-cover" />
    </button>
  );
}

function FileChip({ att }: { att: Attachment }) {
  const { download, busy } = useDownloader();
  return (
    <button
      type="button"
      disabled={busy}
      onClick={() => download(att)}
      className="inline-flex max-w-full items-center gap-2 rounded-md border bg-card px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
    >
      <Paperclip className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
      <span className="truncate">{att.fileName}</span>
      <span className="text-xs text-muted-foreground shrink-0">{formatSize(att.sizeBytes)}</span>
      <Download className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
    </button>
  );
}
